// src/pages/regions/RegionsPage.tsx
import {useMemo, useState} from "react";
import {Plus, Search, Filter, Download, Trash2, Edit} from "lucide-react";
import {Button, Input, Select} from "../../components/ui";
import PageHeader from "../../components/common/PageHeader";
import DataTable from "../../components/common/DataTable/DataTable";
import FormModal, {
  type FormFieldConfig,
} from "../../components/common/Forms/FormModal";
import {useRegions} from "../../hooks/useRegions";
import {useTable} from "../../hooks/common/useTable";
import {useModal} from "../../hooks/common/useModal";
import {formatDate} from "../../utils/formatters";
import {
  createCommonActions,
  createCommonBulkActions,
} from "../../components/common/DataTable/TableActions";
import type {
  RegionResponseDto,
  CreateRegionDto,
  UpdateRegionDto,
} from "../../types";
import type {
  DataTableAction,
  DataTableBulkAction,
  DataTableColumn,
} from "../../components/common/DataTable/DataTableTypes";

const RegionsPage: React.FC = () => {
  const tableKey = "regions";

  // Table state management
  const {
    currentPage,
    pageSize,
    sortConfig,
    filters,
    handlePageChange,
    handlePageSizeChange,
    handleSort,
    handleFilterChange,
    getQueryParams,
  } = useTable({key: tableKey});

  // API hooks
  const {
    regions,
    pagination,
    stats,
    isLoading,
    handleCreate,
    handleUpdate,
    handleDelete,
    handleBulkCreate,
    refetch,
  } = useRegions(getQueryParams());

  // Modal state
  const createModal = useModal("create-region");
  const editModal = useModal("edit-region");
  const [selectedRegion, setSelectedRegion] =
    useState<RegionResponseDto | null>(null);

  // Event handlers
  const handleEdit = (region: RegionResponseDto) => {
    setSelectedRegion(region);
    editModal.open();
  };

  const handleDeleteConfirm = async (region: RegionResponseDto) => {
    if (
      window.confirm(`Are you sure you want to delete "${region.nameEng}"?`)
    ) {
      await handleDelete(region.id);
      refetch();
    }
  };

  const handleBulkDelete = async (regions: RegionResponseDto[]) => {
    if (
      window.confirm(
        `Are you sure you want to delete ${regions.length} regions?`
      )
    ) {
      // Implement bulk delete
      console.log("Bulk delete:", regions);
    }
  };
  const createInitialValues = useMemo(() => ({}), []);
  const editInitialValues = useMemo(
    () =>
      selectedRegion
        ? {
            nameEng: selectedRegion.nameEng,
            nameMm: selectedRegion.nameMm,
            slug: selectedRegion.slug || "",
          }
        : {},
    [selectedRegion]
  );

  // Table columns configuration
  const columns: DataTableColumn<RegionResponseDto>[] = [
    {
      key: "nameEng",
      title: "English Name",
      sortable: true,
      render: (_, record) => (
        <div>
          <div className="font-medium text-gray-900 dark:text-white">
            {record.nameEng}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {record.slug}
          </div>
        </div>
      ),
    },
    {
      key: "nameMm",
      title: "Myanmar Name",
      sortable: true,
      render: (value) => (
        <div className="text-gray-900 dark:text-white">{value as string}</div>
      ),
    },
    {
      key: "townships",
      title: "Townships",
      align: "center",
      render: (_, record) => {
        // Calculate township count from the townships array
        const townshipCount = Array.isArray(record.townships)
          ? record.townships.length
          : 0;

        return (
          <div className="flex items-center justify-center space-x-2">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100">
              {townshipCount}
            </span>
          </div>
        );
      },
    },
    {
      key: "properties",
      title: "Properties",
      align: "center",
      render: (_, record) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100">
          {(record as any).propertyCount || 0}
        </span>
      ),
    },
    {
      key: "createdAt",
      title: "Created Date",
      sortable: true,
      render: (value) => (
        <div className="text-sm text-gray-500 dark:text-gray-400">
          {formatDate(value as Date)}
        </div>
      ),
    },
    {
      key: "actions",
      title: "Actions",
      align: "center",
      render: (_, record) => (
        <div className="flex items-center justify-center space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleEdit(record)}
            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
          >
            <Edit className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDeleteConfirm(record)}
            className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      ),
    },
  ];

  // Form field configurations
  const createFormFields: FormFieldConfig[] = [
    {
      name: "nameEng",
      label: "English Name",
      type: "text",
      required: true,
      placeholder: "Enter region name in English",
      validation: {
        required: true,
        minLength: 2,
        maxLength: 100,
      },
    },
    {
      name: "nameMm",
      label: "Myanmar Name",
      type: "text",
      required: true,
      placeholder: "Enter region name in Myanmar",
      validation: {
        required: true,
        minLength: 2,
        maxLength: 100,
      },
    },
    {
      name: "slug",
      label: "Slug (Optional)",
      type: "text",
      placeholder: "URL-friendly identifier (auto-generated if empty)",
      helperText: "Leave empty to auto-generate from English name",
      validation: {
        pattern: /^[a-z0-9-]+$/,
        custom: (value: unknown) => {
          const stringValue = value as string;
          if (stringValue && !/^[a-z0-9-]+$/.test(stringValue)) {
            return "Slug must contain only lowercase letters, numbers, and hyphens";
          }
          return null;
        },
      },
    },
  ];

  const editFormFields: FormFieldConfig[] = createFormFields;

  // Handle form submissions
  const handleCreateSubmit = async (values: Record<string, unknown>) => {
    const data: CreateRegionDto = {
      nameEng: values.nameEng as string,
      nameMm: values.nameMm as string,
    };
    if (values.slug && typeof values.slug === "string") {
      data.slug = values.slug;
    }

    const success = await handleCreate(data);
    if (success) {
      createModal.close();
      refetch();
    }
  };

  const handleEditSubmit = async (values: Record<string, unknown>) => {
    if (!selectedRegion) return;
    const data = values as UpdateRegionDto;
    const success = await handleUpdate(selectedRegion.id, data);
    if (success) {
      editModal.close();
      setSelectedRegion(null);
      refetch();
    }
  };

  // Handle search
  const handleSearch = (value: string) => {
    handleFilterChange("search", value);
  };

  const tableData: RegionResponseDto[] = regions.map((region) => ({
    ...region,
    createdAt: new Date(region.createdAt),
    updatedAt: new Date(region.updatedAt),
  }));

  return (
    <div className="space-y-6">
      <PageHeader
        title="Regions Management"
        breadcrumbs={[{label: "Locations", path: "#"}, {label: "Regions"}]}
        action={{
          label: "Add Region",
          onClick: createModal.open,
          icon: <Plus className="w-4 h-4 mr-2" />,
        }}
      />

      {/* Statistics Cards */}
      {(stats || pagination) && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-dark-800 p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Total Regions
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {pagination?.total || 0}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-blue-100 dark:bg-blue-900">
                <div className="w-6 h-6 text-blue-600 dark:text-blue-400">
                  📍
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-dark-800 p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Most Popular
                </p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {stats?.mostPopularRegion || "N/A"}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {stats?.mostPopularRegionCount || 0} properties
                </p>
              </div>
              <div className="p-3 rounded-lg bg-green-100 dark:bg-green-900">
                <div className="w-6 h-6 text-green-600 dark:text-green-400">
                  🏆
                </div>
              </div>
            </div>
          </div>

          {/* <div className="bg-white dark:bg-dark-800 p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Avg Properties
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {stats?.averagePropertiesPerRegion || 0}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-purple-100 dark:bg-purple-900">
                <div className="w-6 h-6 text-purple-600 dark:text-purple-400">
                  📊
                </div>
              </div>
            </div>
          </div> */}

          <div className="bg-white dark:bg-dark-800 p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Without Properties
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {stats?.regionsWithoutProperties || 0}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-yellow-100 dark:bg-yellow-900">
                <div className="w-6 h-6 text-yellow-600 dark:text-yellow-400">
                  ⚠️
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Data Table */}
      <DataTable
        data={tableData}
        columns={columns}
        loading={isLoading}
        rowKey="id"
        pagination={{
          current: currentPage,
          pageSize,
          total: pagination?.total || 0,
          showSizeChanger: true,
          onChange: (page, size) => {
            if (page !== currentPage) {
              handlePageChange(page);
            }
            if (size && size !== pageSize) {
              handlePageSizeChange(size);
            }
          },
        }}
        searchable
        searchPlaceholder="Search regions..."
        onSearch={handleSearch}
        filters={[
          {
            key: "sortBy",
            label: "Sort By",
            type: "select",
            options: [
              {value: "nameEng", label: "English Name"},
              {value: "nameMm", label: "Myanmar Name"},
              {value: "createdAt", label: "Created Date"},
              {value: "updatedAt", label: "Updated Date"},
            ],
            defaultValue: "nameEng",
          },
          {
            key: "sortOrder",
            label: "Sort Order",
            type: "select",
            options: [
              {value: "ASC", label: "Ascending"},
              {value: "DESC", label: "Descending"},
            ],
            defaultValue: "ASC",
          },
        ]}
        onFilter={(filters) => {
          Object.entries(filters).forEach(([key, value]) => {
            handleFilterChange(key, value);
          });
        }}
      />

      {/* Create Region Modal */}
      <FormModal
        isOpen={createModal.isOpen}
        onClose={createModal.close}
        title="Create New Region"
        fields={createFormFields}
        initialValues={createInitialValues}
        onSubmit={handleCreateSubmit}
        submitText="Create Region"
      />

      {/* Edit Region Modal */}
      <FormModal
        isOpen={editModal.isOpen}
        onClose={() => {
          editModal.close();
          setSelectedRegion(null);
        }}
        title="Edit Region"
        fields={editFormFields}
        initialValues={editInitialValues}
        onSubmit={handleEditSubmit}
        submitText="Update Region"
      />
    </div>
  );
};

export default RegionsPage;
